import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiCheckCircle,
  FiClock,
  FiFileText,
  FiMessageSquare,
  FiPlay,
  FiFlag,
  FiAlertCircle
} = FiIcons;

/**
 * Timeline Event Component
 * Displays individual timeline event with icon, title, description, and metadata
 */
const TimelineEvent = ({ event, isLast }) => {
  const getEventIcon = (type) => {
    switch (type) {
      case 'case_started':
        return FiPlay;
      case 'action_completed':
        return FiCheckCircle;
      case 'stage_started':
        return FiPlay;
      case 'stage_completed':
        return FiFlag;
      case 'document_uploaded':
        return FiFileText;
      case 'note_added':
        return FiMessageSquare;
      default:
        return FiClock;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'case_started':
        return 'bg-indigo-100 text-indigo-600 border-indigo-300';
      case 'action_completed':
        return 'bg-green-100 text-green-600 border-green-300';
      case 'stage_started':
        return 'bg-blue-100 text-blue-600 border-blue-300';
      case 'stage_completed':
        return 'bg-purple-100 text-purple-600 border-purple-300';
      case 'document_uploaded':
        return 'bg-gray-100 text-gray-600 border-gray-300';
      case 'note_added':
        return 'bg-yellow-100 text-yellow-600 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-300';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    return formatDate(dateString);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="relative flex items-start space-x-4 pb-8"
    >
      {/* Timeline Line */}
      {!isLast && (
        <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200" />
      )}

      {/* Event Icon */}
      <div className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 flex items-center justify-center ${getEventColor(event.event_type)}`}>
        <SafeIcon icon={getEventIcon(event.event_type)} className="w-5 h-5" />
      </div>

      {/* Event Content */}
      <div className="flex-1 min-w-0">
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">{event.event_title}</h4>
              {event.stage_name && (
                <p className="text-xs text-gray-500 mb-1">
                  Stage: <span className="font-medium">{event.stage_name}</span>
                </p>
              )}
            </div>
            
            {/* Badges */}
            <div className="flex items-center space-x-2 ml-2">
              {event.is_legal && (
                <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 rounded">
                  ⚖️ Legal
                </span>
              )}
              {event.is_acas && (
                <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded">
                  🟢 ACAS
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {event.event_description && (
            <p className="text-sm text-gray-600 mb-3 whitespace-pre-line">
              {event.event_description}
            </p>
          )}

          {/* Timestamp */}
          <div className="flex items-center space-x-2 text-xs text-gray-500">
            <SafeIcon icon={FiClock} className="w-3 h-3" />
            <span>{getTimeAgo(event.event_date)}</span>
            <span className="text-gray-400">•</span>
            <span>{formatDate(event.event_date)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Case Timeline Component
 * Displays chronological timeline of all case events
 */
const CaseTimeline = ({ events, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiClock} className="text-4xl text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Timeline Events</h3>
        <p className="text-gray-600">
          Timeline events will appear here as actions are completed and stages progress.
        </p>
      </div>
    );
  }

  // Sort events by date ascending (oldest first) for chronological reading
  const sortedEvents = [...events].sort((a, b) => 
    new Date(a.event_date) - new Date(b.event_date)
  );

  // Group events by date
  const groupedEvents = sortedEvents.reduce((groups, event) => {
    const date = new Date(event.event_date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
    
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});

  return (
    <div className="space-y-8">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiCheckCircle} className="text-green-600" />
            <span className="text-xs font-medium text-green-900">Actions</span>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {events.filter(e => e.event_type === 'action_completed').length}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiPlay} className="text-blue-600" />
            <span className="text-xs font-medium text-blue-900">Stages Started</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            {events.filter(e => e.event_type === 'stage_started').length}
          </p>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiFlag} className="text-purple-600" />
            <span className="text-xs font-medium text-purple-900">Stages Done</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">
            {events.filter(e => e.event_type === 'stage_completed').length}
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiFileText} className="text-gray-600" />
            <span className="text-xs font-medium text-gray-900">Documents</span>
          </div>
          <p className="text-2xl font-bold text-gray-600">
            {events.filter(e => e.event_type === 'document_uploaded').length}
          </p>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-1">
            <SafeIcon icon={FiMessageSquare} className="text-yellow-600" />
            <span className="text-xs font-medium text-yellow-900">Notes</span>
          </div>
          <p className="text-2xl font-bold text-yellow-600">
            {events.filter(e => e.event_type === 'note_added').length}
          </p>
        </div>
      </div>

      {/* Timeline Events */}
      <div className="space-y-6">
        {Object.entries(groupedEvents).map(([date, dateEvents], dateIndex) => (
          <div key={date}>
            {/* Date Header */}
            <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-4 py-2 mb-4 rounded-lg">
              <h3 className="font-semibold text-gray-900">{date}</h3>
              <p className="text-xs text-gray-600">{dateEvents.length} event{dateEvents.length !== 1 ? 's' : ''}</p>
            </div>

            {/* Events for this date */}
            <div className="ml-4">
              {dateEvents.map((event, eventIndex) => (
                <TimelineEvent
                  key={`${event.case_id}-${event.event_type}-${event.event_date}-${eventIndex}`}
                  event={event}
                  isLast={dateIndex === Object.keys(groupedEvents).length - 1 && eventIndex === dateEvents.length - 1}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CaseTimeline;
